import * as React from "react";
import { useDraggableContainer } from "../../hooks/useDraggableContainer";
import { useElementSize } from "../../hooks/useElementSize";
import { useLoc } from "../../hooks/useLoc";
import { useMonth } from "../../hooks/useMonth";
import { useScrollHandler } from "../../hooks/useScrollHandler";
import { monthSlugsWithOverview } from "../../services/centralStore";
import getLocationPath from "../../services/getLocationPath";
import { WeatherSearchResultRowComplete } from "../../services/weatherSearch";
import { RegionBase } from "../../templates/fragments/region";
import { ResortBase } from "../../templates/fragments/resort";
import { FadedOverlay } from "./base/faded-ovelay";
import { ScrollButtons } from "./base/scroll-buttons";
import { DestinationCard } from "./card";

export const DestinationCards = ({
    regionsOrResorts,
    className,
    useSlugName,
    isResorts,
    includeCountry
}: {
    regionsOrResorts: RegionBase[] | ResortBase[];
    className?: string;
    useSlugName?: boolean;
    isResorts?: boolean;
    includeCountry?: boolean;
}) => {
    return (
        <div
            className={`m-auto flex max-w-[1500px] flex-wrap justify-center gap-[20px]
        ts:px-8 sm:gap-[32px] md:px-12 ${className}`}
        >
            <DestinationCardsInternal
                {...{
                    regionsOrResorts,
                    useSlugName,
                    isResorts,
                    includeCountry
                }}
            />
        </div>
    );
};

export const DestinationCardsSearch = ({
    regions,
    additionalRegions,
    inRow,
    className
}: {
    regions: WeatherSearchResultRowComplete[];
    additionalRegions?: WeatherSearchResultRowComplete[];
    inRow?: boolean;
    className?: string;
}) => {
    const scrollRef = React.useRef<null | any>(null);
    const { needEdgeOverlay, isDragging } = useDraggableContainer(
        scrollRef,
        true
    );
    const firstCard = (scrollRef?.current?.children || [])[0];
    const scrollDiff = useElementSize({ el: firstCard });
    const width = useElementSize({});
    const scrollHandler = useScrollHandler({ ref: scrollRef });
    const onScroll = ({ isLeft }: { isLeft: boolean }) => {
        scrollHandler(
            (scrollDiff + 8) * (isLeft ? -1 : 1) * (width > 400 ? 2 : 1)
        );
    };
    return (
        <DestinationCardsWrapper inRow={inRow}>
            <div
                ref={scrollRef}
                className={
                    inRow
                        ? `flex flex-row gap-x-[6px] overflow-x-auto ${className} ${
                              isDragging ? "scroll-auto" : "scroll-smooth"
                          }`
                        : `m-auto flex flex-wrap gap-y-2 pb-[20px] ${className}`
                }
            >
                <DestinationCardsInternal
                    regionsOrResorts={regions}
                    additionalRegions={additionalRegions}
                    includeCountry={true}
                    forceVerySmall={inRow}
                    forceSmall={true}
                    onHover={(hovered: boolean, slug: string) => {}}
                />
            </div>
            {inRow && regions.length > 2 && (
                <FadedOverlay
                    fadeLeft={needEdgeOverlay.left}
                    fadeRight={needEdgeOverlay.right}
                    isBigger={true}
                ></FadedOverlay>
            )}
            {inRow &&
                regions.length > 2 &&
                (needEdgeOverlay.left || needEdgeOverlay.right) && (
                    <ScrollButtons
                        fadeLeft={needEdgeOverlay.left}
                        fadeRight={needEdgeOverlay.right}
                        onClick={onScroll}
                    />
                )}
        </DestinationCardsWrapper>
    );
};

const DestinationCardsWrapper = ({ inRow, children }) => {
    return inRow ? (
        <div className={`absolute left-0 right-0 h-[188px]`}>{children}</div>
    ) : (
        <>{children}</>
    );
};

const DestinationCardsInternal = ({
    regionsOrResorts,
    additionalRegions,
    useSlugName,
    isResorts,
    includeCountry,
    forceSmall,
    forceVerySmall,
    onHover
}: {
    regionsOrResorts:
        | RegionBase[]
        | ResortBase[]
        | WeatherSearchResultRowComplete[];
    additionalRegions?: WeatherSearchResultRowComplete[];
    useSlugName?: boolean;
    isResorts?: boolean;
    includeCountry?: boolean;
    forceSmall?: boolean;
    forceVerySmall?: boolean;
    onHover?: (hovered: boolean, slug: string) => void;
}) => {
    let { month } = useMonth({ ignoreOverview: true });
    const loc = useLoc();
    const monthIndex = monthSlugsWithOverview.indexOf(month);

    const getDestinationCard = (
        regionOrResort: RegionBase | ResortBase | WeatherSearchResultRowComplete
    ) =>
        regionOrResort.mainImg && (
            <DestinationCard
                imageDataLike={
                    regionOrResort.mainImg.childImageSharp.gatsbyImageData
                }
                partOfTheWorld={loc(
                    `common.continents.${
                        isResorts
                            ? (regionOrResort as ResortBase).region.country
                                  .continent
                            : (regionOrResort as RegionBase).country.continent
                    }`
                )}
                lazyLoad={true}
                key={regionOrResort.slug}
                originalSlug={regionOrResort.slug}
                tags={regionOrResort.tags}
                includeCountry={
                    isResorts || !(regionOrResort as RegionBase).isCountry
                        ? includeCountry
                        : false
                }
                forceSmall={forceSmall}
                forceVerySmall={forceVerySmall}
                countryName={
                    isResorts
                        ? (regionOrResort as ResortBase).region.country.name
                        : (regionOrResort as RegionBase).country.name
                }
                location={
                    useSlugName
                        ? regionOrResort.slugName || regionOrResort.name
                        : regionOrResort.name
                }
                weatherData={regionOrResort.weatherData}
                monthIndexes={
                    (regionOrResort as WeatherSearchResultRowComplete)
                        .monthIndexes
                }
                slug={getLocationPath({
                    location: regionOrResort,
                    isCountryInstance: false,
                    isResortInstance: isResorts,
                    monthIndex
                })}
                onHover={onHover}
            />
        );

    return (
        <>
            {regionsOrResorts &&
                regionsOrResorts.map(
                    (
                        regionOrResort:
                            | RegionBase
                            | ResortBase
                            | WeatherSearchResultRowComplete
                    ) => getDestinationCard(regionOrResort)
                )}

            {additionalRegions?.length && (
                <InsideCardsTextWrapper className="mt-[32px]">
                    Showing also the results outside of the map view:
                </InsideCardsTextWrapper>
            )}
            {additionalRegions &&
                additionalRegions.map(
                    (
                        regionOrResort:
                            | RegionBase
                            | ResortBase
                            | WeatherSearchResultRowComplete
                    ) => getDestinationCard(regionOrResort)
                )}
        </>
    );
};

export const InsideCardsTextWrapper = ({
    children,
    className = ""
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={`my-2 w-full px-2 text-center text-lg font-bold tracking-[0.4px] ${className}`}
        >
            {children}
        </div>
    );
};
